import React, { useEffect, useState } from "react";
import { Button, ProgressBar, Alert, Image } from "react-bootstrap";
import apiClient from "../../utils/apiClient";

const UploadWithS3 = ({
    s3KeyPrefix,
    accept = "image/*",
    onUpload,         // (keys: string[] | string) => void
    onDelete,         // (deletedKey: string) => void
    value,            // string for single, string[] for multiple
    multiple = false  // default: single file
}) => {
    const [progress, setProgress] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [previews, setPreviews] = useState([]);

    const [uploadedKeys, setUploadedKeys] = useState([]);

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        setError("");

        if (multiple) {
            setUploading(true);
            let newPreviews = [...previews];
            let newKeys = [...uploadedKeys];
            for (const f of files) {
                const localUrl = URL.createObjectURL(f);
                const tempKey = `${s3KeyPrefix}/${Date.now()}_${f.name}`;
                newPreviews.push({ key: tempKey, url: localUrl, uploading: true });
                setPreviews([...newPreviews]);
                try {
                    const formData = new FormData();
                    formData.append("file", f);
                    const { data } = await apiClient.post("/files/upload", formData, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    const key = data.key;
                    newKeys.push(key);
                    newPreviews = newPreviews.map(p =>
                        p.key === tempKey ? { ...p, key, uploading: false } : p
                    );
                    setPreviews([...newPreviews]);
                } catch {
                    setError(`Failed to upload: ${f.name}`);
                    newPreviews = newPreviews.filter(p => p.key !== tempKey);
                    setPreviews([...newPreviews]);
                }
            }
            setUploadedKeys(newKeys);
            onUpload && onUpload(newKeys);
            setUploading(false);
        } else {
            if (value) {
                try {
                    await apiClient.delete("/files/delete", { data: { key: value } });
                    onDelete && onDelete(value);
                } catch { }
            }
            const f = files[0];
            const localUrl = URL.createObjectURL(f);
            setPreviews([{ key: null, url: localUrl, uploading: true }]);
            setProgress(0);
            setUploading(true);
            try {
                const formData = new FormData();
                formData.append("file", f);
                const { data } = await apiClient.post("/files/upload", formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: (e) => {
                        if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
                    },
                });
                const key = data.key;
                setProgress(100);
                setPreviews([{ key, url: localUrl, uploading: false }]);
                onUpload && onUpload(key);
            } catch {
                setError("Upload failed");
                setPreviews([]);
            }
            setUploading(false);
        }
    };

    const handleDelete = async (keyToDelete) => {
        try {
            await apiClient.delete("/files/delete", { data: { key: keyToDelete } });
            if (multiple) {
                // Remove from state
                setUploadedKeys(keys => keys.filter(k => k !== keyToDelete));
                setPreviews(previews => previews.filter(p => p.key !== keyToDelete));
                onDelete && onDelete(keyToDelete);
            } else {
                setPreviews([]);
                onDelete && onDelete(keyToDelete);
            }
        } catch {
            setError("Failed to delete");
        }
    };

    useEffect(() => {
        if (!value || (Array.isArray(value) && value.length === 0)) setPreviews([]);
        if (!multiple) setProgress(0);
    }, [value, multiple]);


    // For multiple: value = array; for single: value = string
    // Show previews accordingly
    const filesToShow = multiple ? previews : previews.length ? [previews[0]] : [];

    return (
        <div>
            <input
                type="file"
                accept={accept}
                onChange={handleFileChange}
                disabled={uploading}
                multiple={multiple}
            />
            {uploading && !multiple && <ProgressBar now={progress} label={`${progress}%`} className="mt-2" />}
            <div className="d-flex flex-wrap gap-2 mt-2">
                {filesToShow.map(
                    (p, idx) =>
                        p && (
                            <div key={p.key || idx} className="d-flex align-items-center gap-2 mb-2">
                                {accept.startsWith("image") ? (
                                    <Image
                                        src={p.url}
                                        style={{ maxWidth: 80, maxHeight: 80, borderRadius: 8 }}
                                        thumbnail
                                    />
                                ) : (
                                    <a href={p.url} target="_blank" rel="noopener noreferrer">View File</a>
                                )}
                                {p.uploading && <small className="text-muted ms-1">(Uploading...)</small>}
                                <Button
                                    variant="danger"
                                    size="sm"
                                    onClick={() => handleDelete(p.key)}
                                    disabled={p.uploading}
                                >
                                    Delete
                                </Button>
                            </div>
                        )
                )}
            </div>
            {error && <Alert variant="danger" className="mt-2 py-1">{error}</Alert>}
        </div>
    );
};

export default UploadWithS3;
